import * as XLSX from 'xlsx';
import {
  extractMetadataOptions,
  findLookupById,
  findLookupByName,
  parseCsvText,
} from '../assigner-bulk-upload/assigner-bulk-upload.util';
import { filterSupplementFssaiCategories } from '../metadata/fssai-domain.util';
import {
  createDefaultSupBulkSelections,
  SUP_BULK_MAX_ROWS,
  SUP_BULK_NET_QTY_UNITS,
  SUP_BULK_SHELF_LIFE_UNITS,
  SUP_BULK_TEMPLATE_HEADERS,
  SUP_BULK_TEMPLATE_SAMPLE_ROW,
  SupBulkEntitySelections,
  SupBulkFieldError,
  SupBulkIngredientDraft,
  SupBulkLookups,
  SupBulkLookupOption,
  SupBulkNutrientItem,
  SupBulkParseResult,
  SupBulkParsedText,
  SupBulkPreviewRow,
  SupBulkRowSelections,
  SupBulkSkuDraft,
} from './super-admin-supplement-bulk-upload.models';

function normalizeHeader(header: string): string {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
}

function cell(row: Record<string, string>, ...aliases: string[]): string {
  for (const alias of aliases) {
    const key = normalizeHeader(alias);
    const match = Object.keys(row).find((k) => normalizeHeader(k) === key);
    if (match != null && row[match] != null) {
      return String(row[match]).trim();
    }
  }
  return '';
}

function csvEscape(value: string): string {
  const raw = String(value ?? '');
  if (/[",\n\r]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
}

export function downloadSuperAdminSupplementTemplate(
  format: 'csv' | 'xlsx' = 'csv'
): void {
  const headers = [...SUP_BULK_TEMPLATE_HEADERS];
  const sample = SUP_BULK_TEMPLATE_HEADERS.map((h) => SUP_BULK_TEMPLATE_SAMPLE_ROW[h] ?? '');
  const baseName = 'SuperAdmin_RDMS_Supplement_Template';

  if (format === 'xlsx') {
    const sheet = XLSX.utils.aoa_to_sheet([headers, sample]);
    sheet['!cols'] = headers.map((h) => ({ wch: Math.min(36, Math.max(14, h.length + 2)) }));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Supplements');
    const guide = XLSX.utils.aoa_to_sheet([
      ['RDMS Supplement Bulk Upload — Template Guide'],
      [''],
      ['Required columns', 'None — all columns optional. Empty product name imports as Untitled Supplement (Row N).'],
      ['Formats', 'Upload .xlsx, .xls, or .csv.'],
      ['Any form OK', 'Free text ingredients/macros/claims/SKU are accepted. Master fields accept ID and/or Name.'],
      ['Master match', 'When a value matches master it fills the dropdown; otherwise the text is still saved.'],
      ['SKU', 'SKUDetails free text OR Name|NetWt|MRP|… — both work.'],
      ['Lists', 'Use commas/semicolons freely. Optional structured form: Type|Text;;Type|Text'],
      ['Limit', `Maximum ${SUP_BULK_MAX_ROWS} rows per file (same as recipe bulk upload).`],
    ]);
    guide['!cols'] = [{ wch: 28 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(book, guide, 'Guide');
    XLSX.writeFile(book, `${baseName}.xlsx`);
    return;
  }

  const headerLine = headers.join(',');
  const sampleLine = sample.map((v) => csvEscape(v)).join(',');
  const csvContent = `\uFEFF${headerLine}\n${sampleLine}\n`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${baseName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function parseSkuRaw(raw: string): SupBulkSkuDraft[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  // Structured multi-row: Name|NetWt|MRP|…;;…
  if (text.includes(';;') || (text.includes('|') && text.split('|').length >= 2)) {
    return text
      .split(';;')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const pieces = part.split('|').map((p) => p.trim());
        // "SKU-XXX | 100 g | Powder" → name, netWeight (+ unit in same cell), mrp/form
        const netPiece = pieces[1] || '';
        const qtyMatch = netPiece.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
        return {
          name: pieces[0] || '',
          netWeight: qtyMatch ? qtyMatch[1] : netPiece,
          mrp: pieces[2] || '',
          manufacturedBy: pieces[3] || '',
          marketedBy: pieces[4] || '',
          packagedBy: pieces[5] || '',
          unitId: null,
          unitName: qtyMatch?.[2]?.trim() || null,
          countryId: null,
          countryName: null,
          productImage: null,
          productBarcode: null,
        };
      })
      .filter((s) => s.name);
  }

  return [
    {
      name: text,
      netWeight: '',
      mrp: '',
      manufacturedBy: '',
      marketedBy: '',
      packagedBy: '',
      unitId: null,
      unitName: null,
      countryId: null,
      countryName: null,
      productImage: null,
      productBarcode: null,
    },
  ];
}

export function parseIngredientsRaw(raw: string): SupBulkIngredientDraft[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  // Structured: Name|Amount|Unit;;Name|Amount|Unit
  if (text.includes('|') || text.includes(';;')) {
    return text
      .split(';;')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const pieces = part.split('|').map((p) => p.trim());
        return {
          name: pieces[0] || '',
          amount: pieces[1] || '',
          unit: pieces[2] || '',
        };
      })
      .filter((i) => i.name);
  }

  // Free-text: "Whey protein concentrate/isolate, lecithin, permitted flavour and sweetener"
  return splitIngredientFreeText(text)
    .map((part) => {
      const pct = part.match(/^(.*?)\s+(\d+(?:\.\d+)?)\s*%\s*$/i);
      if (pct) return { name: pct[1].trim(), amount: pct[2], unit: '%' };
      const qty = part.match(
        /^(.*?)\s+(\d+(?:\.\d+)?)\s*(g|gm|grams?|kg|mg|ml|l|mcg|µg|ug|serving|capsule|tablet|scoop)\s*$/i
      );
      if (qty) {
        return { name: qty[1].trim(), amount: qty[2], unit: qty[3] };
      }
      return { name: part, amount: '', unit: '' };
    })
    .filter((i) => i.name && i.name.length > 1);
}

/** Split free-text ingredient lists on ; or , while respecting parentheses/brackets. */
function splitIngredientFreeText(text: string): string[] {
  const cleaned = text.replace(/^ingredients?\s*[:\-]\s*/i, '').trim();
  const sep = cleaned.includes(';') ? ';' : ',';
  const out: string[] = [];
  let buf = '';
  let depth = 0;
  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if (ch === '(' || ch === '[') depth++;
    if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
    if (ch === sep && depth === 0) {
      const piece = buf.trim();
      if (piece) out.push(piece);
      buf = '';
      continue;
    }
    buf += ch;
  }
  const last = buf.trim();
  if (last) out.push(last);
  return out;
}

export function parseNutrientRaw(raw: string): SupBulkNutrientItem[] {
  const text = String(raw || '').trim();
  if (!text) return [];

  if (text.includes('|') || text.includes(';;')) {
    return text
      .split(';;')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const pieces = part.split('|').map((p) => p.trim());
        return {
          nutrientId: '',
          nutrientName: pieces[0] || '',
          unitId: '',
          unitName: pieces[1] || '',
          amount: pieces[2] || '',
          percentDV: pieces[3] || '',
          rda: pieces[4] || '',
        };
      })
      .filter((n) => n.nutrientName);
  }

  // Free text: "Per 100 g: Energy 370–410 kcal; Protein 70–80 g; …"
  const cleaned = text
    .replace(/^per\s+\d+(?:\.\d+)?\s*(g|kg|ml|l)\s*:\s*/i, '')
    .replace(/\.$/, '');
  const parts = cleaned
    .split(/[;,]|(?:\s+and\s+)/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const out: SupBulkNutrientItem[] = [];
  for (const part of parts) {
    const m = part.match(
      /^(.+?)\s+(\d+(?:\.\d+)?(?:\s*[–\-]\s*\d+(?:\.\d+)?)?)\s*(kcal|kj|g|gm|grams?|mg|mcg|µg|ug|%|iu)?\s*$/i
    );
    if (!m) continue;
    const amountRaw = m[2].replace(/\s*[–\-]\s*/g, '-');
    // Prefer the upper end of a range for a single numeric amount.
    const amount = amountRaw.includes('-')
      ? amountRaw.split('-').pop()!.trim()
      : amountRaw;
    out.push({
      nutrientId: '',
      nutrientName: m[1].trim(),
      unitId: '',
      unitName: (m[3] || '').trim(),
      amount,
      percentDV: '',
      rda: '',
    });
  }
  return out.filter((n) => n.nutrientName);
}

function parseRowText(raw: Record<string, string>): SupBulkParsedText {
  return {
    companyName: cell(raw, 'CompanyName', 'Company Name'),
    address: cell(raw, 'Address'),
    executiveOwner: cell(raw, 'ExecutiveOwner', 'Executive Owner'),
    mobileNumber: cell(raw, 'MobileNumber', 'Mobile Number'),
    email: cell(raw, 'Email', 'EmailId'),
    gstStatus: cell(raw, 'GstStatus', 'GST Status'),
    brandName: cell(raw, 'BrandName', 'Brand Name'),
    productName: cell(raw, 'ProductName', 'Product Name'),
    brandWebsite: cell(raw, 'BrandWebsite', 'Brand Website'),
    instagram: cell(raw, 'Instagram'),
    facebook: cell(raw, 'Facebook'),
    linkedin: cell(raw, 'LinkedIn', 'Linkedin'),
    shelfLife: cell(raw, 'ShelfLife', 'Shelf Life'),
    netQuantity: cell(raw, 'NetQuantity', 'Net Quantity'),
    lotNumber: cell(raw, 'LotNumber', 'Lot Number'),
    referenceUrl: cell(raw, 'ReferenceUrl', 'Reference URL'),
    availableFlavour: cell(raw, 'AvailableFlavour', 'Available Flavour'),
    supplementDescription: cell(raw, 'SupplementDescription', 'Description'),
    dataCapturingDate: cell(raw, 'DataCapturingDate', 'Data Capturing Date'),
    dateOfManufacturing: cell(raw, 'DateOfManufacturing', 'Date of Manufacturing'),
    dateOfExpiry: cell(raw, 'DateOfExpiry', 'Date of Expiry'),
    bestBeforeDate: cell(raw, 'BestBeforeDate', 'Best Before Date'),
    manufacturedByName: cell(raw, 'ManufacturedByName', 'Manufactured By Name'),
    manufacturedByAddress: cell(raw, 'ManufacturedByAddress', 'Manufactured By Address'),
    manufacturedByLicenseNo: cell(raw, 'ManufacturedByLicenseNo', 'Manufactured By License No'),
    marketedByName: cell(raw, 'MarketedByName', 'Marketed By Name'),
    marketedByAddress: cell(raw, 'MarketedByAddress', 'Marketed By Address'),
    marketedByLicenseNo: cell(raw, 'MarketedByLicenseNo', 'Marketed By License No'),
    packagedByName: cell(raw, 'PackagedByName', 'Packaged By Name'),
    packagedByAddress: cell(raw, 'PackagedByAddress', 'Packaged By Address'),
    packagedByLicenseNo: cell(raw, 'PackagedByLicenseNo', 'Packaged By License No'),
    country: cell(raw, 'Country', 'CompanyCountry'),
    state: cell(raw, 'State', 'CompanyState'),
    city: cell(raw, 'City', 'CompanyCity'),
    pincode: cell(raw, 'Pincode', 'PinCode', 'Pin Code', 'CompanyPincode'),
    dietaryPreference: cell(raw, 'DietaryPreference', 'Dietary Preference'),
    productForm: cell(raw, 'ProductForm', 'Product Form', 'SupplementForm'),
    dataSource: cell(raw, 'DataSource', 'Data Source'),
    databaseSource: cell(raw, 'DatabaseSource', 'Database Source'),
    countryOfOrigin: cell(raw, 'CountryOfOrigin', 'Country of Origin'),
    shelfLifeUnit: cell(raw, 'ShelfLifeUnit', 'Shelf Life Unit'),
    netQuantityUnit: cell(raw, 'NetQuantityUnit', 'Net Quantity Unit'),
    fssaiCategory: cell(raw, 'FssaiCategory', 'FSSAI Category'),
    eStoreCategory: cell(raw, 'EStoreCategory', 'E-Store Category', 'EStore Category'),
    tastes: cell(raw, 'Tastes', 'Taste'),
    flavours: cell(raw, 'Flavours', 'Flavors', 'Flavour'),
    manufacturedByCountry: cell(raw, 'ManufacturedByCountry'),
    manufacturedByState: cell(raw, 'ManufacturedByState'),
    manufacturedByCity: cell(raw, 'ManufacturedByCity'),
    manufacturedByPincode: cell(raw, 'ManufacturedByPincode'),
    marketedByCountry: cell(raw, 'MarketedByCountry'),
    marketedByState: cell(raw, 'MarketedByState'),
    marketedByCity: cell(raw, 'MarketedByCity'),
    marketedByPincode: cell(raw, 'MarketedByPincode'),
    packagedByCountry: cell(raw, 'PackagedByCountry'),
    packagedByState: cell(raw, 'PackagedByState'),
    packagedByCity: cell(raw, 'PackagedByCity'),
    packagedByPincode: cell(raw, 'PackagedByPincode'),
    skuRaw: cell(raw, 'SKUDetails', 'SkuDetails', 'SKU'),
    ingredientsRaw: cell(raw, 'Ingredients'),
    recommendedDosageAmount: cell(raw, 'RecommendedDosageAmount', 'DosageAmount'),
    recommendedDosageUnit: cell(raw, 'RecommendedDosageUnit', 'DosageUnit'),
    recommendedDosageNote: cell(raw, 'RecommendedDosageNote', 'DosageNote'),
    macrosRaw: cell(raw, 'Macros', 'Macronutrients'),
    microsRaw: cell(raw, 'Micros', 'Micronutrients'),
    allergenDeclared: cell(raw, 'AllergenDeclared', 'Allergens'),
    mayContain: cell(raw, 'MayContain'),
    producedInFacility: cell(raw, 'ProducedInFacility'),
    allergenDeclaredNotes: cell(raw, 'AllergenDeclaredNotes'),
    mayContainNotes: cell(raw, 'MayContainNotes'),
    producedInFacilityNotes: cell(raw, 'ProducedInFacilityNotes'),
    directionForUse: cell(raw, 'DirectionForUse', 'Direction for Use'),
    warnings: cell(raw, 'Warnings'),
    disclaimer: cell(raw, 'Disclaimer'),
    productClaimText: cell(raw, 'ProductClaimText'),
    productBenefitText: cell(raw, 'ProductBenefitText'),
    ingredientClaimText: cell(raw, 'IngredientClaimText'),
  };
}

function buildDisplay(parsed: SupBulkParsedText): Record<string, string> {
  return {
    BrandName: parsed.brandName,
    ProductName: parsed.productName,
    CompanyName: parsed.companyName,
  };
}

function toDateOrNull(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function toNumberOrNull(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function lookupName(
  options: Array<{ id: string | number; name: string }> | undefined,
  id: string | number | null | undefined
): string | null {
  if (id == null || id === '') return null;
  return findLookupById(options, id)?.name || null;
}

function mapSensory(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string }> {
  return (ids || [])
    .map((id) => {
      const match = findLookupById(options, id);
      return match ? { id: String(match.id), name: match.name } : null;
    })
    .filter(Boolean) as Array<{ id: string; name: string }>;
}

function mapAllergenItems(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string; image: string; allergenType: number; description: string }> {
  return (ids || [])
    .map((id) => {
      const match = findLookupById(options, id);
      return match
        ? { id: String(match.id), name: match.name, image: '', allergenType: 0, description: '' }
        : null;
    })
    .filter(Boolean) as Array<{
    id: string;
    name: string;
    image: string;
    allergenType: number;
    description: string;
  }>;
}

function mapObservedItems(
  ids: Array<string | number>,
  options: Array<{ id: string | number; name: string }>
): Array<{ id: string; name: string; image: string; description: string }> {
  return (ids || [])
    .map((id) => {
      const match = findLookupById(options, id);
      return match ? { id: String(match.id), name: match.name, image: '', description: '' } : null;
    })
    .filter(Boolean) as Array<{ id: string; name: string; image: string; description: string }>;
}

function buildEntityPayload(
  name: string,
  address: string,
  licenseNo: string,
  sel: SupBulkEntitySelections
): Record<string, string> {
  return {
    name: name || '',
    address: address || '',
    licenseNo: licenseNo || '',
    countryId: sel?.countryId ? String(sel.countryId) : '',
    stateId: sel?.stateId ? String(sel.stateId) : '',
    cityId: sel?.cityId ? String(sel.cityId) : '',
    pincodeId: sel?.pincodeId ? String(sel.pincodeId) : '',
    stateName: sel?.stateName || '',
    cityName: sel?.cityName || '',
    pincode: sel?.pincode || '',
    licenseImage: sel?.licenseImage || '',
  };
}

function categoryObj(id?: string | null, name?: string | null): { id: string; name: string } | null {
  if (!id) return null;
  return { id: String(id), name: name || '' };
}

function resolveLookupByFuzzyName(
  options: SupBulkLookupOption[] | undefined,
  name: string
): SupBulkLookupOption | null {
  if (!name || !options?.length) return null;
  const needle = name.trim().toLowerCase();
  if (!needle) return null;

  const exact = options.find((o) => String(o.name || '').trim().toLowerCase() === needle);
  if (exact) return exact;

  // Short labels (Fat, Oil) must not fuzzy-match long names like "Monounsaturated Fatty Acids".
  if (needle.length <= 4) {
    const wordHit = options.find((o) => {
      const optionName = String(o.name || '').trim().toLowerCase();
      const words = optionName.split(/[^a-z0-9]+/).filter(Boolean);
      return words.includes(needle);
    });
    if (wordHit) return wordHit;
    return null;
  }

  const byPrefix = options.find((o) => {
    const optionName = String(o.name || '').trim().toLowerCase();
    return optionName.startsWith(needle) || needle.startsWith(optionName);
  });
  if (byPrefix) return byPrefix;

  return (
    options.find((o) => {
      const optionName = String(o.name || '').trim().toLowerCase();
      if (optionName.length < 3) return false;
      return optionName.includes(needle) || needle.includes(optionName);
    }) || null
  );
}

/** Common unit aliases from free-text CSV (Months, softgels, …). */
const UNIT_ALIASES: Record<string, string[]> = {
  month: ['month', 'months', 'mo', 'mth'],
  months: ['month', 'months', 'mo', 'mth'],
  day: ['day', 'days'],
  days: ['day', 'days'],
  year: ['year', 'years', 'yr'],
  years: ['year', 'years', 'yr'],
  softgel: ['softgel', 'softgels', 'soft gel', 'soft gels'],
  softgels: ['softgel', 'softgels', 'soft gel', 'soft gels'],
  capsule: ['capsule', 'capsules', 'cap'],
  tablet: ['tablet', 'tablets', 'tab'],
  serving: ['serving', 'servings', 'serve'],
  g: ['g', 'gm', 'gram', 'grams'],
  mg: ['mg', 'milligram', 'milligrams'],
  ml: ['ml', 'millilitre', 'milliliter'],
};

function resolveUnitLookup(
  options: SupBulkLookupOption[] | undefined,
  raw: string
): SupBulkLookupOption | null {
  const direct = resolveLookupByFuzzyName(options, raw);
  if (direct) return direct;
  const needle = String(raw || '').trim().toLowerCase();
  if (!needle || !options?.length) return null;
  const aliases = UNIT_ALIASES[needle] || [needle];
  for (const alias of aliases) {
    const hit = options.find((o) => {
      const n = String(o.name || '').trim().toLowerCase();
      return n === alias || n.includes(alias) || alias.includes(n);
    });
    if (hit) return hit;
  }
  return null;
}

function resolveNutrientItems(
  items: SupBulkNutrientItem[],
  nutrients: SupBulkLookupOption[],
  units: SupBulkLookupOption[]
): SupBulkNutrientItem[] {
  return (items || [])
    .map((item) => {
      const byId = item.nutrientId ? findLookupById(nutrients, item.nutrientId) : null;
      const byName =
        !byId && item.nutrientName ? resolveLookupByFuzzyName(nutrients, item.nutrientName) : null;
      const nutrient = byId || byName;
      const unitById = item.unitId ? findLookupById(units, item.unitId) : null;
      const unitByName =
        !unitById && item.unitName ? resolveUnitLookup(units, item.unitName) : null;
      const unit = unitById || unitByName;
      return {
        ...item,
        nutrientId: nutrient ? String(nutrient.id) : String(item.nutrientId || ''),
        nutrientName: nutrient?.name || item.nutrientName || '',
        unitId: unit ? String(unit.id) : String(item.unitId || ''),
        unitName: unit?.name || item.unitName || '',
      };
    })
    // Keep name+amount rows even when master id is unresolved (BE persists by name).
    .filter((n) => {
      const hasId = !!String(n.nutrientId || '').trim();
      const hasName = !!String(n.nutrientName || '').trim();
      const hasAmount = n.amount !== '' && n.amount != null;
      return (hasId || hasName) && (hasName || hasAmount);
    });
}

function isPlaceholderText(value: string): boolean {
  const t = String(value || '').trim().toLowerCase();
  if (!t) return true;
  return /^(n\/?a|na|none|nil|null|-|—|–|\.|tbd|todo|pending)$/i.test(t);
}

function appendNote(base: string, extra: string): string {
  const a = String(base || '').trim();
  const b = String(extra || '').trim();
  if (!b || isPlaceholderText(b)) return a;
  if (!a) return b;
  if (a.toLowerCase().includes(b.toLowerCase())) return a;
  return `${a}; ${b}`;
}

/** Common free-text synonyms → master allergen name keys (matched case-insensitively). */
const ALLERGEN_KEY_ALIASES: Array<{ patterns: string[]; masterNames: string[] }> = [
  { patterns: ['milk', 'dairy', 'whey', 'casein', 'lactose'], masterNames: ['milk', 'dairy'] },
  { patterns: ['soy', 'soya', 'soybean', 'soyabean'], masterNames: ['soy', 'soya', 'soybean'] },
  { patterns: ['fish oil', 'fish'], masterNames: ['fish', 'fish oil'] },
  { patterns: ['tree nuts', 'tree nut', 'nuts'], masterNames: ['nuts', 'tree nuts', 'tree nut'] },
  { patterns: ['peanuts', 'peanut'], masterNames: ['peanut', 'peanuts'] },
  { patterns: ['eggs', 'egg'], masterNames: ['egg', 'eggs'] },
  { patterns: ['gluten', 'wheat'], masterNames: ['gluten', 'wheat'] },
  { patterns: ['sesame'], masterNames: ['sesame'] },
  {
    patterns: ['shellfish', 'crustacean', 'crustaceans', 'shrimp', 'prawn'],
    masterNames: ['shellfish', 'crustacean', 'crustaceans'],
  },
  { patterns: ['mustard'], masterNames: ['mustard'] },
  { patterns: ['celery'], masterNames: ['celery'] },
  { patterns: ['lupin', 'lupine'], masterNames: ['lupin', 'lupine'] },
  { patterns: ['sulphites', 'sulfites', 'sulphite', 'sulfite'], masterNames: ['sulphites', 'sulfites'] },
];

/** Match allergen master names / key synonyms inside free-text; ignore filler words. */
function extractAllergenTokens(
  raw: string,
  options: SupBulkLookupOption[] | undefined
): Array<string | number> {
  if (!raw || isPlaceholderText(raw) || !options?.length) return [];
  const lower = raw.toLowerCase();
  const hits: Array<string | number> = [];
  const pushId = (id: string | number) => {
    if (!hits.some((x) => String(x) === String(id))) hits.push(id);
  };

  // 1) Direct master-name hits (longer names first: "Fish Oil" before "Fish").
  const sorted = [...options].sort(
    (a, b) => String(b.name || '').length - String(a.name || '').length
  );
  for (const opt of sorted) {
    const name = String(opt.name || '').trim();
    if (!name || name.length < 3) continue;
    const needle = name.toLowerCase();
    const re = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(needle)}(?=[^a-z0-9]|$)`, 'i');
    if (re.test(lower)) pushId(opt.id);
  }

  // 2) Alias / key-point hits (soy/soya, dairy→Milk, nuts, …).
  for (const group of ALLERGEN_KEY_ALIASES) {
    const patternHit = group.patterns.some((p) => {
      const re = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(p)}(?=[^a-z0-9]|$)`, 'i');
      return re.test(lower);
    });
    if (!patternHit) continue;
    // Prefer exact master name (Milk over Dairy when both exist for milk/dairy aliases).
    const master =
      options.find((o) => {
        const n = String(o.name || '').trim().toLowerCase();
        return group.masterNames.some((mn) => n === mn);
      }) ||
      options.find((o) => {
        const n = String(o.name || '').trim().toLowerCase();
        return group.masterNames.some((mn) => n.includes(mn) || mn.includes(n));
      });
    if (!master) continue;
    // If Milk already selected, skip Dairy from the same milk/dairy alias group.
    if (group.masterNames.includes('milk') || group.masterNames.includes('dairy')) {
      const milkAlready = hits.some((id) => {
        const n = String(findLookupById(options, id)?.name || '')
          .trim()
          .toLowerCase();
        return n === 'milk' || n === 'dairy';
      });
      const masterName = String(master.name || '').trim().toLowerCase();
      if (milkAlready && (masterName === 'milk' || masterName === 'dairy')) continue;
    }
    pushId(master.id);
  }

  return hits.map((id) => {
    const match = options.find((o) => String(o.id) === String(id));
    return match ? match.id : id;
  }).filter((id, _i, arr) => {
    // Prefer Milk over Dairy when both were extracted from the same free-text.
    const name = String(findLookupById(options, id)?.name || '')
      .trim()
      .toLowerCase();
    if (name !== 'dairy') return true;
    return !arr.some((other) => {
      const otherName = String(findLookupById(options, other)?.name || '')
        .trim()
        .toLowerCase();
      return otherName === 'milk';
    });
  });
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Only keep free-text in notes when NO master keys were selected.
 * When Milk/Soy/… chips are filled, filler ("where whey…", "or other allergens…") is ignored.
 */
function leftoverAllergenText(
  raw: string,
  matchedIds: Array<string | number>,
  _options: SupBulkLookupOption[] | undefined
): string {
  const text = String(raw || '').trim();
  if (!text || isPlaceholderText(text)) return '';
  if ((matchedIds || []).length) return '';
  return text;
}

function promoteIngredientDrafts(selections: SupBulkRowSelections): void {
  const drafts = selections.ingredientDrafts || [];
  if (!drafts.length) return;

  if (!(selections.ingredients || []).length) {
    selections.ingredients = drafts.map((d) => ({
      ingredientId: '',
      name: d.name,
      servingUnitMasterId: '',
      unit: d.unit || '—',
      amount: d.amount || '',
      isNew: true,
    }));
    return;
  }

  const existing = new Set(
    (selections.ingredients || []).map((i) => String(i.name || '').trim().toLowerCase())
  );
  drafts.forEach((d) => {
    const key = String(d.name || '').trim().toLowerCase();
    if (!key || existing.has(key)) return;
    selections.ingredients.push({
      ingredientId: '',
      name: d.name,
      servingUnitMasterId: '',
      unit: d.unit || '—',
      amount: d.amount || '',
      isNew: true,
    });
    existing.add(key);
  });
}

/** No field is required for supplement bulk upload. */
export function getSupBulkFieldErrors(
  _parsed: SupBulkParsedText,
  _selections: SupBulkRowSelections
): SupBulkFieldError[] {
  return [];
}

function isRowConfigured(selections: SupBulkRowSelections): boolean {
  // Informational only — never blocks import.
  return !!(
    selections.countryId ||
    selections.dietaryPreferenceId ||
    selections.productForm ||
    selections.dataSourceId ||
    selections.databaseSourceId ||
    selections.recommendedDosageUnitId ||
    toNumberOrNull(selections.recommendedDosageAmount) != null
  );
}

/** Alias used by modal/row-editor after rename pass. */
export function rebuildSupBulkPayload(row: SupBulkPreviewRow, lookups: SupBulkLookups): void {
  rebuildSupplementBulkPayload(row, lookups);
}

export function rebuildSupplementBulkPayload(row: SupBulkPreviewRow, lookups: SupBulkLookups): void {
  const { parsed, selections } = row;

  if (parsed.recommendedDosageAmount && selections.recommendedDosageAmount == null) {
    selections.recommendedDosageAmount = parsed.recommendedDosageAmount;
  }
  if (parsed.recommendedDosageNote && !selections.recommendedDosageNote) {
    selections.recommendedDosageNote = parsed.recommendedDosageNote;
  }

  // Free-text CSV/XLSX ingredient lists → visible rows (master id optional until user resolves).
  promoteIngredientDrafts(selections);

  // Free-text allergen sentences that didn't fully map → keep in notes so data is not lost.
  parsed.allergenDeclaredNotes = appendNote(
    parsed.allergenDeclaredNotes,
    leftoverAllergenText(parsed.allergenDeclared, selections.allergenDeclaredIds, lookups.allergens)
  );
  parsed.mayContainNotes = appendNote(
    parsed.mayContainNotes,
    leftoverAllergenText(parsed.mayContain, selections.mayContainIds, lookups.allergens)
  );
  parsed.producedInFacilityNotes = appendNote(
    parsed.producedInFacilityNotes,
    leftoverAllergenText(
      parsed.producedInFacility,
      selections.producedInFacilityIds,
      lookups.allergens
    )
  );

  row.configured = isRowConfigured(selections);
  row.valid = true;
  row.error = undefined;

  const isAssignmentStatus =
    selections.status === 'Assigned to Maker' || selections.status === 'Assigned to Checker';

  const dietaryName = lookupName(lookups.dietaryPreferences, selections.dietaryPreferenceId);
  const dataSourceName = lookupName(lookups.dataSources, selections.dataSourceId);
  const databaseSourceName = lookupName(lookups.databaseSources, selections.databaseSourceId);
  const countryOfOriginName = lookupName(lookups.countries, selections.countryOfOriginId);
  const supplementFormId = selections.productForm != null ? String(selections.productForm) : '';
  const dosageUnitId =
    selections.recommendedDosageUnitId != null ? String(selections.recommendedDosageUnitId) : '';
  const dosageUnitName = lookupName(lookups.dosageUnits, selections.recommendedDosageUnitId) || '';
  const dosageAmount = toNumberOrNull(selections.recommendedDosageAmount);

  const productName =
    (parsed.productName || '').trim() ||
    (parsed.brandName || '').trim() ||
    `Untitled Supplement (Row ${row.rowNumber})`;
  const brandName = (parsed.brandName || '').trim() || productName;
  const companyName = (parsed.companyName || '').trim() || brandName;

  // Persist free-text ingredients in description when no master-linked rows exist.
  const hasLinkedIngredients = (selections.ingredients || []).some(
    (i) => !!String(i.ingredientId || '').trim()
  );
  let supplementDescription = parsed.supplementDescription || '';
  if (parsed.ingredientsRaw && !hasLinkedIngredients) {
    supplementDescription = appendNote(
      supplementDescription,
      `Ingredients: ${parsed.ingredientsRaw}`
    );
  }

  const shelfLifeUnitResolved =
    resolveUnitLookup(lookups.units, selections.shelfLifeUnit || '') ||
    resolveUnitLookup(lookups.dosageUnits, selections.shelfLifeUnit || '');
  const netQuantityUnitResolved =
    resolveUnitLookup(lookups.units, selections.netQuantityUnit || '') ||
    resolveUnitLookup(lookups.dosageUnits, selections.netQuantityUnit || '');
  const shelfLifeUnitId = shelfLifeUnitResolved
    ? String(shelfLifeUnitResolved.id)
    : /^\d+$/.test(String(selections.shelfLifeUnit || '').trim())
      ? String(selections.shelfLifeUnit).trim()
      : '';
  const netQuantityUnitId = netQuantityUnitResolved
    ? String(netQuantityUnitResolved.id)
    : /^\d+$/.test(String(selections.netQuantityUnit || '').trim())
      ? String(selections.netQuantityUnit).trim()
      : '';

  const macros = resolveNutrientItems(
    selections.macronutrients || [],
    lookups.nutrients,
    lookups.units?.length ? lookups.units : lookups.dosageUnits
  );
  const micros = resolveNutrientItems(
    selections.micronutrients || [],
    lookups.nutrients,
    lookups.units?.length ? lookups.units : lookups.dosageUnits
  );

  const mapClaimPayload = (
    rows: typeof selections.productClaims,
    idKey: 'claimTypeId' | 'productBenefitId'
  ) =>
    (rows || [])
      .filter((c) => {
        const typeId = String(c.claimTypeId || '').trim();
        const hasText = !!String(c.claimText || '').trim();
        const hasTags = (c.selectedTagIds || []).length > 0;
        return !!typeId && (hasText || hasTags);
      })
      .map((c) => ({
        [idKey]: String(c.claimTypeId),
        ...(idKey === 'claimTypeId'
          ? { claimTypeName: c.claimTypeName || '' }
          : { productBenefitName: c.claimTypeName || '' }),
        claimText: String(c.claimText || '').trim(),
        selectedTagIds: (c.selectedTagIds || []).map((id) => String(id)),
        selectedTagNames: (c.selectedTagNames || []).map((n) => String(n)),
      }));

  const productClaims = mapClaimPayload(selections.productClaims, 'claimTypeId');
  const productBenefitClaims = mapClaimPayload(selections.productBenefitClaims, 'productBenefitId');
  const ingredientClaims = mapClaimPayload(selections.ingredientClaims, 'claimTypeId');

  const nutrientTagGroups = (selections.healthConditionTags || [])
    .filter((g) => g.groupId && (g.tags || []).length)
    .map((g) => ({
      groupId: String(g.groupId),
      groupName: g.groupName || '',
      tags: (g.tags || []).map((t) => ({ id: String(t.id), name: t.name || '' })),
    }));

  row.payload = {
    status: isAssignmentStatus ? 'To be assigned' : selections.status || 'Draft',
    productType: 'Supplement',
    productForm: supplementFormId,

    companyName: companyName,
    address: parsed.address,
    countryId: String(selections.countryId ?? ''),
    stateId: String(selections.stateId ?? ''),
    cityId: String(selections.cityId ?? ''),
    pincodeId: String(selections.pincodeId ?? '').trim(),
    stateName: selections.stateName || parsed.state || '',
    cityName: selections.cityName || parsed.city || '',
    pincode: selections.pincode || parsed.pincode || '',
    executiveOwner: parsed.executiveOwner,
    mobileNumber: parsed.mobileNumber,
    emailId: parsed.email,
    gstStatus: parsed.gstStatus || '',

    dietaryPreference:
      selections.dietaryPreferenceId != null
        ? String(selections.dietaryPreferenceId)
        : parsed.dietaryPreference || '',
    dietaryPreferenceName: dietaryName || parsed.dietaryPreference || '',
    brandName: brandName,
    productName: productName,
    brandWebsite: parsed.brandWebsite || '',
    instagram: parsed.instagram || '',
    facebook: parsed.facebook || '',
    linkedin: parsed.linkedin || '',
    dataSource: selections.dataSourceId != null ? String(selections.dataSourceId) : parsed.dataSource || '',
    dataSourceId: selections.dataSourceId != null ? String(selections.dataSourceId) : '',
    dataSourceName: dataSourceName || parsed.dataSource || '',
    databaseSource:
      selections.databaseSourceId != null
        ? String(selections.databaseSourceId)
        : parsed.databaseSource || '',
    databaseSourceId: selections.databaseSourceId != null ? String(selections.databaseSourceId) : '',
    databaseSourceName: databaseSourceName || parsed.databaseSource || '',
    countryOfOrigin: selections.countryOfOriginId != null ? String(selections.countryOfOriginId) : '',
    countryOfOriginId: selections.countryOfOriginId != null ? String(selections.countryOfOriginId) : '',
    countryOfOriginName: countryOfOriginName || '',
    shelfLife: toNumberOrNull(parsed.shelfLife),
    shelfLifeUnit: shelfLifeUnitId || selections.shelfLifeUnit || '',
    shelfLifeUnitId: shelfLifeUnitId || selections.shelfLifeUnit || '',
    dataCapturingDate: toDateOrNull(parsed.dataCapturingDate),
    dateOfManufacturing: toDateOrNull(parsed.dateOfManufacturing),
    dateOfExpiry: toDateOrNull(parsed.dateOfExpiry),
    bestBeforeDate: toDateOrNull(parsed.bestBeforeDate),
    netQuantity: toNumberOrNull(parsed.netQuantity),
    netQuantityUnit: netQuantityUnitId || selections.netQuantityUnit || '',
    netQuantityUnitId: netQuantityUnitId || selections.netQuantityUnit || '',
    lotNumber: parsed.lotNumber || '',
    referenceUrl: parsed.referenceUrl || '',
    availableFlavour: parsed.availableFlavour || '',
    servingMeasurements: [],
    frontImage: selections.frontImage || '',
    backImage: selections.backImage || '',
    leftImage: selections.leftImage || '',
    rightImage: selections.rightImage || '',

    manufacturedBy: buildEntityPayload(
      parsed.manufacturedByName,
      parsed.manufacturedByAddress,
      parsed.manufacturedByLicenseNo,
      selections.manufacturedBy
    ),
    marketedBy: buildEntityPayload(
      parsed.marketedByName,
      parsed.marketedByAddress,
      parsed.marketedByLicenseNo,
      selections.marketedBy
    ),
    packagedBy: buildEntityPayload(
      parsed.packagedByName,
      parsed.packagedByAddress,
      parsed.packagedByLicenseNo,
      selections.packagedBy
    ),

    skuDetails: (selections.skuDetails || []).map((s) => ({
      name: s.name || '',
      netWeight: s.netWeight || '',
      unitId: s.unitId != null ? String(s.unitId) : '',
      unitName: s.unitName || '',
      productBarcode: s.productBarcode || '',
      mrp: s.mrp || '',
      manufacturedBy: s.manufacturedBy || '',
      marketedBy: s.marketedBy || '',
      packagedBy: s.packagedBy || '',
      productImage: s.productImage || '',
    })),

    fssaiCategory: categoryObj(selections.fssaiCategoryId, selections.fssaiCategoryName) ||
      (parsed.fssaiCategory ? { id: '', name: parsed.fssaiCategory } : null),
    fssaiSubCategory: categoryObj(selections.fssaiSubCategoryId, selections.fssaiSubCategoryName),
    fssaiSubSubCategory: categoryObj(selections.fssaiSubSubCategoryId, selections.fssaiSubSubCategoryName),
    eStoreCategory: categoryObj(selections.eStoreCategoryId, selections.eStoreCategoryName) ||
      (parsed.eStoreCategory ? { id: '', name: parsed.eStoreCategory } : null),
    eStoreSubCategory: categoryObj(selections.eStoreSubCategoryId, selections.eStoreSubCategoryName),
    eStoreSubSubCategory: categoryObj(selections.eStoreSubSubCategoryId, selections.eStoreSubSubCategoryName),

    tastes: mapSensory(selections.tasteIds, lookups.tastes),
    flavours: (() => {
      const mapped = mapSensory(selections.flavourIds, lookups.flavours);
      if (mapped.length) return mapped;
      // Keep free-text flavour when master id is missing so Orange etc. still persist via availableFlavour.
      return [];
    })(),

    allergenInformation: {
      allergenDeclared: {
        allergens: mapAllergenItems(selections.allergenDeclaredIds, lookups.allergens),
        additionalInfo: parsed.allergenDeclaredNotes || '',
      },
      mayContain: {
        allergens: mapAllergenItems(selections.mayContainIds, lookups.allergens),
        additionalInfo: parsed.mayContainNotes || '',
      },
      producedInFacility: {
        allergens: mapAllergenItems(selections.producedInFacilityIds, lookups.allergens),
        additionalInfo: parsed.producedInFacilityNotes || '',
      },
    },
    observedInformation: {
      allergenContains: { items: mapObservedItems(selections.allergenContainsIds, lookups.allergens) },
      allergenFree: { items: mapObservedItems(selections.allergenFreeIds, lookups.allergenFree) },
      intoleranceContains: {
        items: mapObservedItems(selections.intoleranceContainsIds, lookups.intolerances),
      },
      intoleranceFree: {
        items: mapObservedItems(selections.intoleranceFreeIds, lookups.intoleranceFree),
      },
    },
    nutrientInformation: {
      macronutrients: macros,
      micronutrients: micros,
    },
    nutrientTags: {
      nutrientTags: nutrientTagGroups,
    },
    productClaims,
    productBenefitClaims,
    ingredientClaims,
    authorizedCertificateClaims: selections.authorizedCertificateImage
      ? [
          {
            authorizedCertificateId: '',
            authorizedCertificateName: '',
            certificateNumber: '',
            certificateImage: selections.authorizedCertificateImage,
          },
        ]
      : [],
    authorizedLicenseClaims: selections.authorizedLicenseImage
      ? [
          {
            authorizedLicenseId: '',
            authorizedLicenseName: '',
            licenseNumber: '',
            licenseImage: selections.authorizedLicenseImage,
          },
        ]
      : [],
    directionForUse: parsed.directionForUse
      ? [{ directionText: parsed.directionForUse }]
      : [],
    warnings: parsed.warnings ? [{ warningText: parsed.warnings }] : [],
    disclaimer: parsed.disclaimer ? [{ disclaimerText: parsed.disclaimer }] : [],

    supplementDetails: {
      supplementFormId,
      description: supplementDescription,
      // Do not map FSSAI category IDs into supplement main/sub categories —
      // those are different masters and cause wrong/duplicate mappings.
      mainCategoryId: '',
      subCategoryId: '',
      subSubCategoryId: '',
      recommendedDosageAmount: dosageAmount,
      recommendedDosageUnitId: dosageUnitId,
      recommendedDosageNote: selections.recommendedDosageNote || parsed.recommendedDosageNote || '',
      ingredients: (selections.ingredients || [])
        .filter((rowIng) => !!String(rowIng.ingredientId || '').trim())
        .map((rowIng) => ({
          nutraceuticalIngredientMasterId: String(rowIng.ingredientId),
          value: toNumberOrNull(rowIng.amount) ?? 0,
          unit: rowIng.unit && rowIng.unit !== '—' ? rowIng.unit : dosageUnitName || 'mg',
          unitDosageMasterId: rowIng.servingUnitMasterId ? String(rowIng.servingUnitMasterId) : '',
        })),
    },
  };
}

export function refreshSupBulkParseCounts(result: SupBulkParseResult): void {
  result.validCount = result.rows.filter((r) => r.valid).length;
  result.errorCount = result.rows.length - result.validCount;
  result.totalCount = result.rows.length;
}

/** First sheet of .xlsx/.xls → same shape as parseCsvText. */
export function parseSupExcelArrayBuffer(buffer: ArrayBuffer): {
  headers: string[];
  rows: Record<string, string>[];
} {
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return { headers: [], rows: [] };

  const sheet = workbook.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json<(string | number | boolean | null | undefined)[]>(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
  if (!aoa?.length) return { headers: [], rows: [] };

  const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
  if (!headers.some((h) => h.length > 0)) return { headers: [], rows: [] };

  const rows = aoa
    .slice(1)
    .filter((values) => (values || []).some((c) => String(c ?? '').trim().length > 0))
    .map((values) => {
      const record: Record<string, string> = {};
      headers.forEach((header, i) => {
        if (!header) return;
        record[header] = String(values?.[i] ?? '').trim();
      });
      return record;
    });

  return { headers, rows };
}

function buildSupPreviewRows(rows: Record<string, string>[]): SupBulkPreviewRow[] {
  const seenKeys = new Set<string>();
  return rows.map((raw, index) => {
    const parsed = parseRowText(raw);
    const display = buildDisplay(parsed);
    const errors: string[] = [];

    const key = `${parsed.brandName}||${parsed.productName}`.toLowerCase();
    if (parsed.brandName && parsed.productName && seenKeys.has(key)) {
      errors.push(`Duplicate BrandName+ProductName "${parsed.brandName} / ${parsed.productName}" in this file`);
    } else if (parsed.brandName && parsed.productName) {
      seenKeys.add(key);
    }

    const selections = createDefaultSupBulkSelections();
    selections.ingredientDrafts = parseIngredientsRaw(parsed.ingredientsRaw);
    selections.skuDetails = parseSkuRaw(parsed.skuRaw);
    selections.macronutrients = parseNutrientRaw(parsed.macrosRaw);
    selections.micronutrients = parseNutrientRaw(parsed.microsRaw);
    selections.recommendedDosageAmount = parsed.recommendedDosageAmount || null;
    selections.recommendedDosageNote = parsed.recommendedDosageNote || null;

    return {
      rowNumber: index + 1,
      // Duplicates are warnings only — never block import.
      valid: true,
      configured: false,
      error: errors.length ? errors.join('; ') : undefined,
      cells: raw,
      display,
      parsed,
      selections,
    };
  });
}

export function validateSupBulkRows(
  headers: string[],
  rows: Record<string, string>[]
): SupBulkParseResult {
  if (!headers.length) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: 0,
      fileError: 'File is empty or missing headers.',
    };
  }

  if (rows.length > SUP_BULK_MAX_ROWS) {
    return {
      rows: [],
      validCount: 0,
      errorCount: 0,
      totalCount: rows.length,
      fileError: `File exceeds the limit of ${SUP_BULK_MAX_ROWS} rows (${rows.length} found). Please split the file (same cap as recipe bulk upload).`,
    };
  }

  const previewRows = buildSupPreviewRows(rows);
  return {
    rows: previewRows,
    validCount: previewRows.length,
    errorCount: 0,
    totalCount: previewRows.length,
  };
}

export function validateSupBulkCsv(csvText: string): SupBulkParseResult {
  const { headers, rows } = parseCsvText(csvText);
  return validateSupBulkRows(headers, rows);
}

export function validateSupBulkExcel(buffer: ArrayBuffer): SupBulkParseResult {
  const { headers, rows } = parseSupExcelArrayBuffer(buffer);
  return validateSupBulkRows(headers, rows);
}

function matchEnumValue(value: string, options: string[]): string | null {
  const needle = String(value || '').trim().toLowerCase();
  if (!needle) return null;
  return options.find((o) => o.toLowerCase() === needle) || null;
}

function splitNameList(raw: string): string[] {
  return String(raw || '')
    .split(/[;|,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function matchIdsByNames(
  raw: string,
  options: SupBulkLookupOption[] | undefined
): Array<string | number> {
  return splitNameList(raw)
    .map((name) => findLookupByName(options, name))
    .filter(Boolean)
    .map((o) => o!.id);
}

/** Always extract key allergen points from free-text; ignore filler sentences. */
function resolveAllergenIdsFromText(
  raw: string,
  options: SupBulkLookupOption[] | undefined
): Array<string | number> {
  const text = String(raw || '').trim();
  if (!text || isPlaceholderText(text)) return [];

  // Prefer key-point extract (Milk / Fish / Soy / Nuts from long sentences).
  const extracted = extractAllergenTokens(text, options);
  const resolved = extracted.length ? extracted : matchIdsByNames(text, options);

  // Coerce to the exact option.id type/value so ng-select chips resolve labels.
  return coerceIdsToLookupOptions(resolved, options);
}

function coerceIdsToLookupOptions(
  ids: Array<string | number>,
  options: SupBulkLookupOption[] | undefined
): Array<string | number> {
  if (!options?.length) return [];
  const out: Array<string | number> = [];
  for (const id of ids || []) {
    const match = options.find((o) => String(o.id) === String(id));
    if (!match) continue;
    if (!out.some((x) => String(x) === String(match.id))) out.push(match.id);
  }
  return out;
}

function applyEntityLocationHints(
  sel: SupBulkEntitySelections,
  country: string,
  state: string,
  city: string,
  pincode: string,
  countries: SupBulkLookupOption[]
): void {
  if (country) {
    const match = findLookupByName(countries, country);
    if (match) {
      sel.countryId = String(match.id);
      sel.countryName = match.name;
    } else {
      sel.countryName = country;
    }
  }
  if (state) sel.stateName = state;
  if (city) sel.cityName = city;
  if (pincode) sel.pincode = pincode;
}

export function applySupCsvMasterMappings(row: SupBulkPreviewRow, lookups: SupBulkLookups): void {
  const p = row.parsed;
  const s = row.selections;

  const shelfUnit = matchEnumValue(p.shelfLifeUnit, SUP_BULK_SHELF_LIFE_UNITS);
  if (shelfUnit) s.shelfLifeUnit = shelfUnit;

  const netUnit = matchEnumValue(p.netQuantityUnit, SUP_BULK_NET_QTY_UNITS);
  if (netUnit) s.netQuantityUnit = netUnit;

  if (p.productForm) {
    const form = findLookupByName(lookups.supplementForms, p.productForm);
    if (form) s.productForm = String(form.id);
  }

  const dietary = findLookupByName(lookups.dietaryPreferences, p.dietaryPreference);
  if (dietary) s.dietaryPreferenceId = dietary.id;

  const dataSource = findLookupByName(lookups.dataSources, p.dataSource);
  if (dataSource) s.dataSourceId = dataSource.id;

  const databaseSource = findLookupByName(lookups.databaseSources, p.databaseSource);
  if (databaseSource) s.databaseSourceId = databaseSource.id;

  const origin = findLookupByName(lookups.countries, p.countryOfOrigin);
  if (origin) s.countryOfOriginId = origin.id;

  const fssai = findLookupByName(lookups.fssaiCategories, p.fssaiCategory);
  if (fssai) {
    s.fssaiCategoryId = String(fssai.id);
    s.fssaiCategoryName = fssai.name;
  }

  const eStore = findLookupByName(lookups.eStoreCategories, p.eStoreCategory);
  if (eStore) {
    s.eStoreCategoryId = String(eStore.id);
    s.eStoreCategoryName = eStore.name;
  }

  if (p.country) {
    const country = findLookupByName(lookups.countries, p.country);
    if (country) {
      s.countryId = String(country.id);
      s.countryName = country.name;
    } else {
      s.countryName = p.country;
    }
  }
  if (p.state) s.stateName = p.state;
  if (p.city) s.cityName = p.city;
  if (p.pincode) s.pincode = p.pincode;

  applyEntityLocationHints(
    s.manufacturedBy,
    p.manufacturedByCountry,
    p.manufacturedByState,
    p.manufacturedByCity,
    p.manufacturedByPincode,
    lookups.countries
  );
  applyEntityLocationHints(
    s.marketedBy,
    p.marketedByCountry,
    p.marketedByState,
    p.marketedByCity,
    p.marketedByPincode,
    lookups.countries
  );
  applyEntityLocationHints(
    s.packagedBy,
    p.packagedByCountry,
    p.packagedByState,
    p.packagedByCity,
    p.packagedByPincode,
    lookups.countries
  );

  if (p.tastes) {
    const tastes = matchIdsByNames(p.tastes, lookups.tastes);
    if (tastes.length) s.tasteIds = tastes;
  }
  if (p.flavours) {
    const flavours = matchIdsByNames(p.flavours, lookups.flavours);
    if (flavours.length) s.flavourIds = flavours;
    else {
      const extracted = extractAllergenTokens(p.flavours, lookups.flavours);
      if (extracted.length) s.flavourIds = extracted;
    }
  }
  // Prefer AvailableFlavour when Flavours column empty / unmatched
  if (!(s.flavourIds || []).length && p.availableFlavour) {
    const byName = matchIdsByNames(p.availableFlavour, lookups.flavours);
    if (byName.length) s.flavourIds = byName;
    else {
      const extracted = extractAllergenTokens(p.availableFlavour, lookups.flavours);
      if (extracted.length) s.flavourIds = extracted;
    }
  }

  // Free-text allergen sentences: extract master tokens (Milk/Fish/Soy…) instead of fuzzy phrase match.
  if (p.allergenDeclared) {
    s.allergenDeclaredIds = resolveAllergenIdsFromText(p.allergenDeclared, lookups.allergens);
  }
  if (p.mayContain) {
    s.mayContainIds = resolveAllergenIdsFromText(p.mayContain, lookups.allergens);
  }
  if (p.producedInFacility) {
    s.producedInFacilityIds = resolveAllergenIdsFromText(p.producedInFacility, lookups.allergens);
  }

  // Resolve SKU unit names from free-text net weight cells ("60 softgels").
  (s.skuDetails || []).forEach((sku) => {
    if (!sku.unitId && sku.unitName) {
      const unit =
        resolveUnitLookup(lookups.units, sku.unitName) ||
        resolveUnitLookup(lookups.dosageUnits, sku.unitName);
      if (unit) {
        sku.unitId = String(unit.id);
        sku.unitName = unit.name;
      }
    }
  });

  if (p.recommendedDosageUnit) {
    const dosageUnit =
      findLookupByName(lookups.dosageUnits, p.recommendedDosageUnit) ||
      findLookupByName(lookups.units, p.recommendedDosageUnit);
    if (dosageUnit) s.recommendedDosageUnitId = dosageUnit.id;
  }

  s.macronutrients = resolveNutrientItems(
    s.macronutrients || [],
    lookups.nutrients,
    lookups.units?.length ? lookups.units : lookups.dosageUnits
  );
  s.micronutrients = resolveNutrientItems(
    s.micronutrients || [],
    lookups.nutrients,
    lookups.units?.length ? lookups.units : lookups.dosageUnits
  );

  // Promote free-text ingredient drafts into visible ingredient rows early.
  promoteIngredientDrafts(s);

  if (p.productClaimText && !(s.productClaims || []).length && (lookups.claimTypes || []).length) {
    const claimType = lookups.claimTypes[0];
    s.productClaims = [
      {
        claimTypeId: String(claimType.id),
        claimTypeName: claimType.name,
        claimText: p.productClaimText,
        selectedTagIds: [],
        selectedTagNames: [],
      },
    ];
  }
  if (p.productBenefitText && !(s.productBenefitClaims || []).length && (lookups.productBenefits || []).length) {
    const benefit = lookups.productBenefits[0];
    s.productBenefitClaims = [
      {
        claimTypeId: String(benefit.id),
        claimTypeName: benefit.name,
        claimText: p.productBenefitText,
        selectedTagIds: [],
        selectedTagNames: [],
      },
    ];
  }
}

export function applySupCsvMasterMappingsToRows(rows: SupBulkPreviewRow[], lookups: SupBulkLookups): void {
  (rows || []).forEach((row) => applySupCsvMasterMappings(row, lookups));
}

function mapOpts(list: any[]): SupBulkLookupOption[] {
  return extractMetadataOptions(list)
    .map((o) => ({ id: o.id, name: o.name }))
    .filter((o) => o.id != null && o.id !== '' && !!o.name);
}

export function mapSupMetadataToLookups(
  meta: any,
  countryMeta: any,
  recipeMeta?: any,
  extra?: {
    supplementForms?: any[];
    dosageUnits?: any[];
    certificateOptions?: any[];
    licenseOptions?: any[];
  }
): SupBulkLookups {
  const root = meta?.response ?? meta?.data ?? meta ?? {};
  const countryRoot = countryMeta?.response ?? countryMeta?.data ?? countryMeta ?? {};
  const recipeRoot = recipeMeta?.response ?? recipeMeta?.data ?? recipeMeta ?? {};

  const countries =
    countryRoot.countries ||
    countryRoot.Countries ||
    countryRoot.items ||
    (Array.isArray(countryRoot) ? countryRoot : []);

  return {
    dietaryPreferences: mapOpts(
      recipeRoot.dietaryPreferences || recipeRoot.DietaryPreferences || root.dietaryPreferences
    ),
    dataSources: mapOpts(root.ingredientItemTypes || root.IngredientItemTypes || root.dataSources),
    databaseSources: mapOpts(root.recipeLinkSources || root.RecipeLinkSources || root.databaseSources),
    countries: mapOpts(Array.isArray(countries) ? countries : []),
    tastes: mapOpts(recipeRoot.tastes || recipeRoot.Tastes || root.tastes),
    flavours: mapOpts(recipeRoot.flavours || recipeRoot.Flavours || root.flavours),
    allergens: mapOpts(
      root.ingredientAllergens ||
        root.IngredientAllergens ||
        recipeRoot.allergens ||
        recipeRoot.Allergens ||
        root.allergens
    ),
    allergenFree: mapOpts(recipeRoot.allergenFree || recipeRoot.AllergenFree || root.allergenFree),
    intolerances: mapOpts(recipeRoot.intolerances || recipeRoot.Intolerances || root.intolerances),
    intoleranceFree: mapOpts(
      recipeRoot.intoleranceFree || recipeRoot.IntoleranceFree || root.intoleranceFree
    ),
    fssaiCategories: mapOpts(
      filterSupplementFssaiCategories(root.fssaiCategories || root.FssaiCategories || [])
    ),
    eStoreCategories: mapOpts(root.eStoreCategories || root.EStoreCategories || []),
    units: mapOpts(root.units || root.Units || root.servingUnits || []),
    dosageUnits: mapOpts(extra?.dosageUnits || root.unitDosageMasters || root.UnitDosageMasters || []),
    supplementForms: mapOpts(extra?.supplementForms || []),
    nutrients: mapOpts(root.fssaiNutrients || root.FssaiNutrients || root.nutrients || root.Nutrients || []),
    nutrientGroups: mapOpts(root.nutrientGroups || root.NutrientGroups || []),
    nutrientTags: (Array.isArray(root.nutrientTags || root.NutrientTags)
      ? root.nutrientTags || root.NutrientTags
      : []
    )
      .map((item: any) => ({
        id: item?.id ?? item?.Id ?? '',
        name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
        groupId:
          item?.groupId ?? item?.GroupId ?? item?.parentId ?? item?.ParentId ?? item?.nutrientGroupId ?? null,
      }))
      .filter((o: any) => o.id !== '' && !!o.name),
    claimTypes: mapOpts(
      root.supplementClaimTypes ||
        root.SupplementClaimTypes ||
        root.claimTypes ||
        root.ClaimTypes ||
        []
    ),
    claimTags: (Array.isArray(root.claimTags || root.ClaimTags) ? root.claimTags || root.ClaimTags : [])
      .map((item: any) => ({
        id: item?.id ?? item?.Id ?? '',
        name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
        claimTypeId: item?.claimTypeId ?? item?.ClaimTypeId ?? item?.parentId ?? item?.ParentId ?? null,
      }))
      .filter((o: any) => o.id !== '' && !!o.name),
    productBenefits: mapOpts(root.productBenefits || root.ProductBenefits || []),
    productBenefitTags: (Array.isArray(root.productBenefitTags || root.ProductBenefitTags)
      ? root.productBenefitTags || root.ProductBenefitTags
      : []
    )
      .map((item: any) => ({
        id: item?.id ?? item?.Id ?? '',
        name: String(item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? '').trim(),
        productBenefitId:
          item?.productBenefitId ?? item?.ProductBenefitId ?? item?.parentId ?? item?.ParentId ?? null,
      }))
      .filter((o: any) => o.id !== '' && !!o.name),
    certificateOptions: mapOpts(extra?.certificateOptions || root.authorizedCertificates || []),
    licenseOptions: mapOpts(extra?.licenseOptions || root.authorizedLicenses || []),
  };
}

export function mapSupplementSnapshotToLookups(snapshot: any): SupBulkLookups {
  const mapList = (list: any[]): SupBulkLookupOption[] =>
    (list || [])
      .map((item) => ({
        id: String(item?.id ?? item?.Id ?? '').trim(),
        name: String(
          item?.name ??
            item?.Name ??
            item?.label ??
            item?.Label ??
            item?.allergenName ??
            item?.AllergenName ??
            ''
        ).trim(),
      }))
      .filter((o) => o.id !== '' && !!o.name);

  return {
    dietaryPreferences: mapList(snapshot?.dietaryPreferences),
    dataSources: mapList(snapshot?.dataSources),
    databaseSources: mapList(snapshot?.databaseSources),
    countries: mapList(snapshot?.countries?.length ? snapshot.countries : snapshot?.countriesOfOrigin),
    tastes: mapList(snapshot?.tasteList),
    flavours: mapList(snapshot?.flavourList),
    allergens: mapList(snapshot?.allergenMasterList),
    allergenFree: mapList(snapshot?.allergenFreeList),
    intolerances: mapList(snapshot?.intoleranceMasterList),
    intoleranceFree: mapList(snapshot?.intoleranceFreeList),
    fssaiCategories: mapList(snapshot?.fssaiCategories),
    eStoreCategories: mapList(snapshot?.eStoreCategories),
    units: mapList(snapshot?.units),
    dosageUnits: mapList(snapshot?.nutrientUnitOptions),
    supplementForms: mapList(snapshot?.supplementFormOptions),
    nutrients: mapList(snapshot?.fssaiNutrientOptions || snapshot?.macroNutrientOptions),
    nutrientGroups: mapList(snapshot?.nutrientGroups),
    nutrientTags: (snapshot?.nutrientTags || []).map((item: any) => ({
      id: item?.id ?? '',
      name: String(item?.name ?? '').trim(),
      groupId: item?.groupId ?? item?.nutrientGroupId ?? item?.parentId ?? null,
    })),
    claimTypes: mapList(snapshot?.claimTypes),
    claimTags: (snapshot?.claimTags || []).map((item: any) => ({
      id: item?.id ?? '',
      name: String(item?.name ?? '').trim(),
      claimTypeId: item?.claimTypeId ?? item?.parentId ?? null,
    })),
    productBenefits: mapList(snapshot?.productBenefits),
    productBenefitTags: (snapshot?.productBenefitTags || []).map((item: any) => ({
      id: item?.id ?? '',
      name: String(item?.name ?? '').trim(),
      productBenefitId: item?.productBenefitId ?? item?.parentId ?? null,
    })),
    certificateOptions: mapList(snapshot?.certificateOptions),
    licenseOptions: mapList(snapshot?.licenseOptions),
  };
}

export function applyGlobalStatusToSupRows(rows: SupBulkPreviewRow[], status: string): void {
  rows.forEach((row) => {
    row.selections.status = status;
    if (status !== 'Assigned to Maker' && status !== 'Assigned to Checker') {
      row.selections.assignment = null;
    }
  });
}
